import { useEffect, useState, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  AccountBalance as AccountIcon,
  Folder as FolderIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { apiPost } from "../../../../services/api/apiClient";

import {
  fetchVendorInsuranceDetail,
  clearInsuranceDetail,
} from "../../../../redux/slices/vendorsSlice";
import {
  getDropboxTemporaryLink,
  getDropboxPreview,
} from "../../../../redux/slices/dropboxFoldersSlice";
import { ThumbnailPreview } from "../../dropbox-folders/components/drawer/ThumbnailPreview";
import {
  isPdfFile,
  isVideoFile,
  isPreviewableAsPdf,
  isPreviewableAsHtml,
  isImageFile,
} from "../../dropbox-folders/hooks/useFileOperations";

const DRAWER_WIDTH = 480;

interface VendorDetailDrawerProps {
  open: boolean;
  vendorId: string | null;
  year: number;
  onClose: () => void;
  onVendorUpdated?: () => void;
}

const VendorDetailDrawer = ({ open, vendorId, year, onClose, onVendorUpdated }: VendorDetailDrawerProps) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    insuranceDetail,
    insuranceDetailStatus,
    insuranceDetailError,
  } = useAppSelector((state) => state.vendors) as any;

  // Editable vendor email (mirrors the detail's email until edited/saved)
  const [emailInput, setEmailInput] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  // Preview — reuses ThumbnailPreview + dropboxFolders Redux state (same as dropbox-folders page)
  const [previewFile, setPreviewFile] = useState<{ path: string; name: string } | null>(null);
  const previewDrawerOpen = previewFile !== null;

  const {
    thumbnails,
    singleThumbnails,
    temporaryLinks,
    previews,
    temporaryLinkLoading,
    previewLoading,
    thumbnailsLoading,
  } = useAppSelector((state) => state.dropboxFolders) as any;

  const resetPreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  const handleClose = useCallback(() => {
    resetPreview();
    onClose();
  }, [resetPreview, onClose]);

  useEffect(() => {
    if (open && vendorId) {
      dispatch(fetchVendorInsuranceDetail({ vendorId, year }));
    }
    return () => {
      dispatch(clearInsuranceDetail());
    };
  }, [open, vendorId, year, dispatch]);

  useEffect(() => {
    if (insuranceDetailStatus === "failed" && insuranceDetailError) {
      enqueueSnackbar(`Failed to load vendor details: ${insuranceDetailError}`, {
        variant: "error",
      });
    }
  }, [insuranceDetailStatus, insuranceDetailError, enqueueSnackbar]);

  // Keep the email input in sync when a (new) vendor detail loads.
  useEffect(() => {
    if (insuranceDetail?.email !== undefined) {
      setEmailInput(insuranceDetail.email || "");
    }
  }, [insuranceDetail?.email]);

  const handleSaveEmail = useCallback(async () => {
    const newEmail = emailInput.trim();
    const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    if (!EMAIL_REGEX.test(newEmail)) {
      enqueueSnackbar("Please enter a valid email address", { variant: "error" });
      return;
    }
    setEmailSaving(true);
    try {
      await apiPost({
        endpoint: "/update-vendor-email",
        data: { vendorId: insuranceDetail?.id, email: newEmail },
        withAuth: true,
      });
      enqueueSnackbar("Vendor email updated", { variant: "success" });
      setEmailInput(newEmail);
      // Refresh the audit table so the new email shows in the list too.
      onVendorUpdated?.();
    } catch (err: any) {
      enqueueSnackbar(`Failed to update email: ${err.message || "Unknown error"}`, {
        variant: "error",
      });
    } finally {
      setEmailSaving(false);
    }
  }, [emailInput, insuranceDetail?.id, enqueueSnackbar, onVendorUpdated]);

  const emailDirty = emailInput.trim() !== (insuranceDetail?.email || "");

  const handlePreview = useCallback((filePath: string, fileName: string) => {
    if (!filePath) return;
    setPreviewFile({ path: filePath, name: fileName });

    // Dispatch the same Redux actions used by dropbox-folders, so ThumbnailPreview reads the data
    if (isPdfFile(fileName) || isVideoFile(fileName)) {
      dispatch(getDropboxTemporaryLink({ filePath }));
    } else if (isPreviewableAsPdf(fileName) || isPreviewableAsHtml(fileName)) {
      dispatch(getDropboxPreview({ filePath }));
    } else if (isImageFile(fileName)) {
      dispatch(getDropboxTemporaryLink({ filePath }));
      dispatch(getDropboxPreview({ filePath }));
    } else {
      dispatch(getDropboxPreview({ filePath }));
    }
  }, [dispatch]);

  const handleDownload = useCallback(async (filePath: string, fileName: string) => {
    if (!filePath) return;
    try {
      const result = await apiPost({
        endpoint: "/get-dropbox-download",
        data: { filePath },
        withAuth: true,
      });
      if (result.file) {
        // Decode base64 and download
        const byteCharacters = atob(result.file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        enqueueSnackbar(`Downloaded ${fileName}`, { variant: "success" });
      }
    } catch (err: any) {
      enqueueSnackbar(`Failed to download: ${err.message}`, { variant: "error" });
    }
  }, [enqueueSnackbar]);

  const detail: any = insuranceDetail;

  return (
    <>
      {/* Main Vendor Detail Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(19, 21, 23, 0.45)",
          },
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: DRAWER_WIDTH },
            maxWidth: "100%",
            mt: 7,
            borderRadius: 0,
            height: "calc(100% - 56px)",
            backgroundColor: "var(--surface-container-lowest, #ffffff)",
            border: "1px solid var(--outline-variant, #dcdcdc)",
            transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
          },
        }}
      >
        <Box
          sx={{
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Close Button */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2,
            }}
          >
            <IconButton onClick={handleClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              pr: 7,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Vendor Details
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {insuranceDetailStatus === "loading" && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {insuranceDetailStatus === "failed" && (
              <Alert severity="error">{insuranceDetailError}</Alert>
            )}

            {detail && (
              <>
                {/* Vendor Identity */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {detail.name}
                  </Typography>
                  <Chip
                    label={detail.archived ? "Archived" : "Active"}
                    color={detail.archived ? "error" : "success"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                  {detail.shortName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      aka {detail.shortName}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Contact Info */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                  Contact Information
                </Typography>
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <EmailIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <TextField
                      size="small"
                      fullWidth
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="No email"
                      disabled={emailSaving}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                    <Tooltip title="Save email">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleSaveEmail}
                          disabled={
                            emailSaving || !emailDirty || !emailInput.trim()
                          }
                          sx={{ ml: 0.5 }}
                        >
                          {emailSaving ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SaveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItem>
                  {detail.phone && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <PhoneIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={detail.phone} />
                    </ListItem>
                  )}
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <BadgeIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primary={`Account #: ${detail.accountNumber || "-"}`} />
                  </ListItem>
                  {detail.accountType && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AccountIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={`Type: ${detail.accountType}`} />
                    </ListItem>
                  )}
                </List>

                <Divider sx={{ my: 2 }} />

                {/* Dropbox Folder */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                  Dropbox Folder
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <FolderIcon color={detail.hasDropboxFolder ? "success" : "disabled"} fontSize="small" />
                  <Typography variant="body2">
                    {detail.hasDropboxFolder ? detail.dropboxFolderName : "No folder linked"}
                  </Typography>
                </Box>

                {/* Insurance Files */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                  Insurance Certificates ({detail.insuranceFiles?.length || 0})
                </Typography>

                {(!detail.insuranceFiles || detail.insuranceFiles.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>
                    No insurance certificate files found for this vendor.
                  </Typography>
                )}

                {detail.insuranceFiles?.map((file: any, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      mb: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 0,
                      bgcolor: "background.paper",
                    }}
                  >
                    <PdfIcon color="error" fontSize="small" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                        {file.fileName}
                      </Typography>
                      <Chip
                        label={file.fileType === "WC" ? "Workers Comp" : "General Liability"}
                        color={file.fileType === "WC" ? "warning" : "secondary"}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    {file.filePath && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(file.filePath, file.fileName)}
                            disabled={previewLoading}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.filePath, file.fileName)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Payment Activity */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                  Payment Activity
                </Typography>
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ReceiptIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${detail.invoiceCount || 0} paid invoice(s)`}
                      secondary={`Total: $${(detail.totalPaid || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                    />
                  </ListItem>
                  {detail.earliestPayment && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CalendarIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={`First payment: ${detail.earliestPayment}`} />
                    </ListItem>
                  )}
                  {detail.latestPayment && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CalendarIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={`Last payment: ${detail.latestPayment}`} />
                    </ListItem>
                  )}
                  {!detail.hasPayments && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <MoneyIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary="No payment history" secondary="No paid invoices found" />
                    </ListItem>
                  )}
                </List>
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Preview Drawer — reuses ThumbnailPreview from dropbox-folders */}
      <Drawer
        anchor="right"
        open={previewDrawerOpen}
        hideBackdrop
        onClose={resetPreview}
        sx={{
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          "& .MuiDrawer-paper": {
            backgroundColor: "var(--surface-container-lowest, #ffffff)",
            border: "1px solid var(--outline-variant, #dcdcdc)",
            transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
            width: previewFile && (isPdfFile(previewFile.name) || isVideoFile(previewFile.name) || isPreviewableAsPdf(previewFile.name) || isPreviewableAsHtml(previewFile.name))
              ? { xs: "100%", sm: `calc(100vw - ${DRAWER_WIDTH}px)` }
              : { xs: "100%", sm: 400 },
            maxWidth: "100%",
            mt: 7,
            borderRadius: 0,
            height: "calc(100% - 56px)",
            right: { xs: 0, sm: DRAWER_WIDTH },
          },
        }}
      >
        <ThumbnailPreview
          selectedFile={previewFile}
          thumbnailPreviewOpen={previewDrawerOpen}
          onClose={resetPreview}
          thumbnails={thumbnails}
          singleThumbnails={singleThumbnails}
          temporaryLinks={temporaryLinks}
          previews={previews}
          temporaryLinkLoading={temporaryLinkLoading}
          previewLoading={previewLoading}
          thumbnailsLoading={thumbnailsLoading}
        />
      </Drawer>
    </>
  );
};

export default VendorDetailDrawer;
